import { clsx } from "clsx";
import { ChevronDownIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslations } from "../../i18n";
import { Select } from "../Select";
import type { ISelectOption } from "../Select/types";
import { Skeleton } from "../Skeleton";
import { Text } from "../Text";
import { usePaginationContext } from "./context";

export const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];

export const PageSizeControls = () => {
  const { t } = useTranslations();
  const { page, pageSize, totalItems, onPageSizeChange, loading, disabled } =
    usePaginationContext();

  const options: ISelectOption[] = useMemo(() => {
    return DEFAULT_PAGE_SIZE_OPTIONS.map((size) => ({
      id: size.toString(),
      text: size.toString(),
    }));
  }, []);

  const value = pageSize.toString();

  const handleChange = useCallback(
    (nextPageSize: string | undefined) => {
      if (onPageSizeChange) {
        onPageSizeChange(Number(nextPageSize));
      }
    },
    [onPageSizeChange],
  );

  const itemsFrom = (page - 1) * pageSize + 1;
  const itemsTo = Math.min(page * pageSize, totalItems);

  const onRenderAnchor = useCallback(
    (open?: boolean) => {
      return (
        // Presentational: the Select's combobox is the control.
        <span className="nuco-pagination__anchor">
          <Text as="span" className="nuco-pagination__value">
            {value}
          </Text>
          <ChevronDownIcon
            className={clsx(
              "nuco-pagination__chevron",
              open && "nuco-pagination__chevron--open",
            )}
            size="0.875rem"
          />
        </span>
      );
    },
    [value],
  );

  if (loading) {
    return <Skeleton className="nuco-pagination__skeleton--size-controls" />;
  }

  if (!onPageSizeChange) {
    return null;
  }

  return (
    <div className="nuco-pagination__size-controls">
      <Text
        as="span"
        className="nuco-pagination__label nuco-pagination__label--show"
      >
        {t("ui.pagination.show")}
      </Text>

      <Select
        options={options}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        size="sm"
        onRenderAnchor={onRenderAnchor}
        ariaLabel={t("ui.pagination.itemsPerPage")}
        matchTriggerWidth={false}
        minWidth={100}
      />

      <Text
        as="span"
        className="nuco-pagination__label nuco-pagination__label--count"
      >
        {t("ui.pagination.itemsRange", {
          from: itemsFrom,
          to: itemsTo,
          total: totalItems,
        })}
      </Text>
    </div>
  );
};
